import { Icon } from "@iconify/react";
import { motion, useReducedMotion } from "motion/react";
import logo from "@/assets/images/elevintia-logo.svg";
import { useHardwareAcceleration } from "@/hooks/use-hardware-acceleration";
import RevealText from "../ui/reveal-text";
import CopyEmailButton, { CONTACT_EMAIL } from "../ui/copy-email-button";

const footerLinks = [
  {
    label: "LinkedIn",
    value: "@elevintia",
    href: "https://www.linkedin.com/company/elevintia",
  },
  {
    label: "Telegram",
    value: "@elevintia",
    href: "https://t.me/elevintia",
  },
  {
    label: "X",
    value: "@elevintia",
    href: "https://x.com/elevintia",
  },
  {
    label: "YouTube",
    value: "@elevintia",
    href: "https://youtube.com/@elevintia",
  },
];

export default function MobileContact() {
  const reduceMotion = useReducedMotion();
  const hasHardwareAcceleration = useHardwareAcceleration();
  const motionDisabled = Boolean(reduceMotion) || !hasHardwareAcceleration;

  return (
    <section
      id="contact"
      data-mobile-section="contact"
      className="h-[calc(100svh-4.5rem)] min-h-[540px] scroll-mt-18 px-4"
    >
      <div className="mx-auto flex h-full w-full max-w-lg flex-col pb-5 pt-10">
        <motion.div
          initial={{ opacity: 0, y: motionDisabled ? 0 : 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: motionDisabled ? 0 : 0.42 }}
          className="mt-auto pb-7"
        >
          <RevealText
            as="h2"
            trigger="inView"
            text="Let's build what's next."
            className="mb-6 text-lg font-light text-white/70"
          />
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="group flex max-w-full items-center gap-2 text-[clamp(1.45rem,6.7vw,2rem)] font-light leading-none tracking-[-0.05em] focus-visible:outline-none"
          >
            <span>{CONTACT_EMAIL}</span>
            <Icon
              icon="material-symbols:arrow-outward"
              className="shrink-0 text-[0.58em] text-white/48"
            />
          </a>
          <CopyEmailButton className="mt-5" />
        </motion.div>

        <nav
          aria-label="Contact and social links"
          className="grid grid-cols-2 border-y border-white/15"
        >
          {footerLinks.map((link, index) => (
            <motion.a
              key={link.label}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: motionDisabled ? 0 : 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{
                duration: motionDisabled ? 0 : 0.32,
                delay: motionDisabled ? 0 : index * 0.04,
              }}
              className={`flex min-w-0 items-center justify-between gap-2 py-4 focus-visible:bg-white/[0.035] focus-visible:outline-none ${
                index % 2 === 0 ? "border-r border-white/15 pr-4" : "pl-4"
              } ${index < 2 ? "border-b border-white/15" : ""}`}
            >
              <span className="min-w-0">
                <span className="block text-[10px] text-white/42">
                  {link.label}
                </span>
                <span className="mt-1 block truncate text-sm font-light text-white/82">
                  {link.value}
                </span>
              </span>
              <Icon
                icon="material-symbols:arrow-outward"
                className="shrink-0 text-base text-white/38"
              />
            </motion.a>
          ))}
        </nav>

        <footer className="mt-6 flex items-center justify-between gap-4 text-[10px] text-white/40">
          <img
            src={logo}
            alt="Elevintia"
            className="h-6 select-none opacity-75"
            draggable={false}
          />
          <p className="text-right">© 2026 Elevintia. All rights reserved.</p>
        </footer>
      </div>
    </section>
  );
}
